import { asRecord, numberValue, stringValue } from '../services/api';
import type { HearthTagSemantic } from '../components/HearthTag';
import { formatBusinessDateTime } from '../utils/businessTime';
import { positionLabel } from '../utils/position';
import { adminRoleFromPosition, type AdminProfile } from './AdminScopeContext';

export type QrLoginState = 'idle' | 'creating' | 'waiting' | 'exchanging' | 'success' | 'expired' | 'denied' | 'error';

export type AdminLoginStatus = 'pending' | 'confirmed' | 'expired' | 'denied' | 'consumed';

export type AdminLoginTicket = {
  ticket: string;
  status: AdminLoginStatus;
  wxacode_url: string;
  expires_in_seconds: number;
  polling_interval_seconds: number;
  /** 绝对过期时刻 (ISO)。⚠️ **倒计时绝不以它为锚** —— 那会把用户的本机时钟变成登录的
   *  必要条件: 电脑时钟慢一小时, 每张新码一生成就被判"已过期", 人直接登不进来。
   *  倒计时锚 expires_in_seconds(服务端相对 TTL) + 本地已流逝时长, 见
   *  ticketRemainingSeconds。本字段只用于展示/调试, 不参与任何"是否过期"的判断。 */
  expires_at: string;
  /** 「本次请求」上下文 (后端 admin-login-confirm 定稿 2026-07-11 暴露的三项)。
   *  mp 确认页 (pages/admin-login-confirm) 给用户看的就是这三项 —— 电脑端同屏回显,
   *  用户两边一对才敢确认, 防钓鱼这一环才闭合。后端明确不下发短确认码, 也不下发地理位置。 */
  device_label: string | null;
  requester_ip: string | null;
  requested_at: string | null;
  /** 确认后后端回填的登录身份 (成功态短暂驻留时显示"是谁登进来了")。 */
  actor: AdminLoginActor | null;
};

/** 票据上的登录身份预览 (后端 AdminLoginActorPreview 的前端投影)。 */
export type AdminLoginActor = {
  fullName: string;
  positionCn: string;
  storeNameCn: string;
  isSuperAdmin: boolean;
};

/** 「本次请求」上下文行 (与 mp admin-login-confirm 的 requestContext 同字段同标签)。 */
export type LoginContextRow = {
  key: 'device' | 'ip' | 'at';
  label: string;
  value: string;
  unknown: boolean;
};

export type AdminLoginExchange = {
  jwt: string;
};

export type ActorBadgeStatus = 'default' | 'processing' | 'error' | 'warning' | 'success';

export function positiveStoreId(value: unknown): number | null {
  const candidate = typeof value === 'string' ? Number(value) : value;
  return typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0 ? candidate : null;
}

export function mapProfile(response: unknown): AdminProfile {
  const payload = asRecord(asRecord(response).data ?? response);
  // 072 多店挂载 (2026-07-09 admin 切店器): 容错解析, 缺字段/坏行恒退 []。
  const managedRaw = Array.isArray(payload.managed_stores) ? payload.managed_stores : [];
  const managedStores = managedRaw
    .map((item) => {
      const row = asRecord(item);
      const storeId = numberValue(row.store_id, 0);
      const name = stringValue(row.store_name_cn, '');
      if (storeId <= 0 || !name) return null;
      return { storeId, storeNameCn: name, isPrimary: row.is_primary === true };
    })
    .filter((item): item is AdminProfile['managedStores'][number] => item !== null);
  return {
    fullName: stringValue(payload.full_name, '已连接账号'),
    positionCn: positionLabel(
      payload.role ?? payload.position,
      payload.role_cn ?? payload.position_cn ?? '角色待确认',
    ),
    role: adminRoleFromPosition(payload.role ?? payload.position),
    storeName: stringValue(payload.store_name_cn, ''),
    storeId: positiveStoreId(payload.store_id),
    isSuperAdmin: payload.is_super_admin === true,
    employeeId: numberValue(payload.employee_id, 0) || null,
    managedStores,
  };
}

export function isUnsupportedScope(profile: AdminProfile): boolean {
  return (
    !profile.isSuperAdmin &&
    (profile.role === 'unknown' ||
      profile.role === 'staff' ||
      profile.role === 'hq_ops' ||
      (profile.role === 'store_manager' && profile.storeId === null))
  );
}

export function buildActorScope(profile: AdminProfile | null): string {
  if (!profile) return '';
  if (profile.isSuperAdmin) {
    // 2026-07-06 Ricky 拍板: 超管=全权限, 不再显示自己档案绑定的门店
    // (查看哪家店由顶部「查看门店」切店器决定, 与账号档案解耦).
    return '全权限';
  }
  if (profile.role === 'finance') return '全部直营';
  if (profile.role === 'owner' || profile.role === 'area_manager') return '上级审批范围';
  if (profile.storeId === null) return '未分配可操作门店';
  return profile.storeName || `门店 ${profile.storeId}`;
}

export function buildActorBadge(input: {
  connected: boolean;
  checking: boolean;
  profileError: string | null;
  profile: AdminProfile | null;
}): ActorBadgeStatus {
  if (!input.connected) return 'default';
  if (input.checking) return 'processing';
  if (input.profileError) return 'error';
  if (input.profile?.isSuperAdmin) return 'warning';
  if (
    input.profile?.storeId === null &&
    input.profile.role !== 'finance' &&
    input.profile.role !== 'owner' &&
    input.profile.role !== 'area_manager'
  ) {
    return 'warning';
  }
  return 'success';
}

export function buildActorText(input: {
  connected: boolean;
  checking: boolean;
  profileError: string | null;
  profile: AdminProfile | null;
}): string {
  if (!input.connected) return '未连接账号';
  if (input.checking) return '校验账号中…';
  if (input.profileError) return '账号不可用';
  if (!input.profile) return '已连接账号';
  const role = input.profile.isSuperAdmin ? '超级管理者' : input.profile.positionCn;
  const scope = buildActorScope(input.profile);
  return `${input.profile.fullName} · ${role}${scope ? ` · ${scope}` : ''}`;
}

function unwrapData(response: unknown): Record<string, unknown> {
  const root = asRecord(response);
  return asRecord(root.data ?? root);
}

export function mapTicket(response: unknown): AdminLoginTicket {
  const payload = unwrapData(response);
  return {
    ticket: stringValue(payload.ticket, ''),
    status: stringValue(payload.status, 'pending') as AdminLoginStatus,
    wxacode_url: stringValue(payload.wxacode_url, ''),
    expires_in_seconds: numberValue(payload.expires_in_seconds),
    polling_interval_seconds: numberValue(payload.polling_interval_seconds, 2),
    expires_at: stringValue(payload.expires_at, ''),
    device_label: payload.device_label ? stringValue(payload.device_label, '') : null,
    requester_ip: payload.requester_ip ? stringValue(payload.requester_ip, '') : null,
    requested_at: payload.requested_at ? stringValue(payload.requested_at, '') : null,
    actor: mapTicketActor(payload.actor),
  };
}

function mapTicketActor(value: unknown): AdminLoginActor | null {
  if (!value || typeof value !== 'object') return null;
  const row = asRecord(value);
  const fullName = stringValue(row.full_name, '');
  if (!fullName) return null;
  return {
    fullName,
    positionCn: positionLabel(row.position_cn, row.position_cn),
    storeNameCn: stringValue(row.store_name_cn, ''),
    isSuperAdmin: row.is_super_admin === true,
  };
}

/** 成功态一行身份文案: "杨鸿杰 · 老板 · 全部直营" (稿 #ae-login 态卡③)。 */
export function loginActorLine(actor: AdminLoginActor | null): string {
  if (!actor) return '';
  const role = actor.isSuperAdmin ? '超级管理者' : actor.positionCn;
  const parts = [actor.fullName, role, actor.storeNameCn].filter((part) => part.length > 0);
  return parts.join(' · ');
}

/**
 * 票据剩余秒数 —— 锚在"服务端给的相对 TTL + 本地已流逝时长"上。
 *
 * 为什么不锚 expires_at (绝对时刻): 那样会把**用户的本地时钟**变成登录的必要条件 ——
 * 电脑时钟慢一小时, 每张新码一生成就被判"已过期", 人直接登不进来 (e2e 用一份旧 fixture
 * 复现了这个死局)。相对 TTL 只依赖"本地经过了多久", 时钟偏了也不影响。
 *
 * 为什么不直接显 expires_in_seconds: 它是"回包那一刻"的快照, 轮询间隔 2s → 数字冻结着
 * 一跳一跳, 看着像卡死。这里用它当起点, 本地每秒推算, 每次轮询回包自动校正。
 *
 * 返回 null = 后端没给 TTL (老后端 / 坏数据) → 不显倒计时, **也绝不判过期**
 * (rev-adm4 担心的"缺字段被误判为已过期"在这里被显式挡掉)。
 */
export function ticketRemainingSeconds(
  ticket: AdminLoginTicket | null,
  receivedAtMs: number,
  nowMs: number,
): number | null {
  if (!ticket) return null;
  const total = ticket.expires_in_seconds;
  if (!Number.isFinite(total) || total <= 0) return null;
  const elapsedSeconds = Math.max(0, (nowMs - receivedAtMs) / 1000);
  return Math.max(0, Math.ceil(total - elapsedSeconds));
}

/** 倒计时文案 mm:ss (稿 "09:38")。 */
export function formatCountdown(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  const mm = Math.floor(safe / 60);
  const ss = safe % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/** 烧退进度条剩余比例 0-100 (与 mp 确认页 remainPercent 同语言)。 */
export function countdownPercent(remainSeconds: number, totalSeconds: number): number {
  if (totalSeconds <= 0) return 0;
  const pct = (remainSeconds / totalSeconds) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

function requestedAtText(iso: string | null): string {
  return formatBusinessDateTime(iso, { seconds: false, fallback: '' });
}

/**
 * 「本次请求」上下文 —— 与 mp admin-login-confirm 页逐项对齐 (设备 / 来源 IP / 发起时间)。
 *
 * 这三项是电脑端与手机端**唯一可以互相核对**的信息: 手机上看到的设备/IP/时间, 必须与
 * 电脑上这块卡一致, 用户才敢按确认。后端已明确不下发短确认码 (schemas.py 注释), 因此
 * 这里不编码、不造数; 缺字段照 mp 的口径显"未知设备 / —"。
 */
export function buildLoginContextRows(ticket: AdminLoginTicket | null): LoginContextRow[] {
  const row = (key: LoginContextRow['key'], label: string, value: string, unknownText: string): LoginContextRow =>
    value ? { key, label, value, unknown: false } : { key, label, value: unknownText, unknown: true };
  return [
    row('device', '设备', ticket?.device_label ?? '', '未知设备'),
    row('ip', '来源 IP', ticket?.requester_ip ?? '', '—'),
    row('at', '发起时间', requestedAtText(ticket?.requested_at ?? null), '—'),
  ];
}

export function mapExchange(response: unknown): AdminLoginExchange {
  const payload = unwrapData(response);
  return {
    jwt: stringValue(payload.jwt, ''),
  };
}

export function buildAbsoluteApiUrl(path: string, baseUrl: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const base = baseUrl.replace(/\/$/, '');
  return `${base}${path}`;
}

export function deviceLabel(platform: string | null | undefined): string {
  return `Ember Admin · ${platform || '电脑端浏览器'}`;
}

export function qrStatusText(state: QrLoginState): string {
  switch (state) {
    case 'creating':
      return '生成中';
    case 'waiting':
      return '等待扫码';
    case 'exchanging':
      return '正在登录';
    case 'success':
      return '登录成功';
    case 'expired':
      return '已过期';
    case 'denied':
      return '手机端已拒绝';
    case 'error':
      return '连接异常';
    default:
      return '未开始';
  }
}

/**
 * 四态收口 (设计稿 frag-adm-e #ae-login 批注④): 等待=info / 成功=success /
 * 拒绝=danger / 过期=muted。
 *
 * 旧映射把"等待扫码"打成 warning (橙) —— 等待不是风险, 却和"整改/临期"同色;
 * 把"已过期"打成 danger (红) —— 过期是自然失效, 不是错误, 稿要 muted (灰) 让它退场,
 * 视线留给码上那块"点击刷新"。error (连接异常) 才是真 danger。
 */
export function qrStatusColor(state: QrLoginState): HearthTagSemantic {
  switch (state) {
    case 'waiting':
    case 'exchanging':
      return 'info'; // 等待扫码 / 正在登录 → 进行中
    case 'success':
      return 'success';
    case 'denied':
    case 'error':
      return 'danger'; // 手机端拒绝 / 连接异常
    case 'expired':
      return 'muted'; // 自然失效 → 退场色, 不抢"点击刷新"的注意力
    default:
      return 'neutral'; // 未开始 / 生成中
  }
}

/** 失效态 (过期 / 拒绝 / 异常): 二维码不再可扫, 要打蒙层 + 原位续码。 */
export function qrIsDead(state: QrLoginState): boolean {
  return state === 'expired' || state === 'denied' || state === 'error';
}

/** 失效蒙层主文案 (稿: 过期/拒绝各有自己的说法, 不共用一句"已失效")。 */
export function qrDeadVeilText(state: QrLoginState): string {
  switch (state) {
    case 'denied':
      return '已在手机上拒绝';
    case 'error':
      return '二维码不可用';
    default:
      return '二维码已过期';
  }
}
